import {
  Badge,
  Box,
  Button,
  Header,
  Link,
  StatusIndicator,
  StatusIndicatorProps,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import {
  PsnsRedriveSummary,
  Stage,
} from "../../../lusca-search-api/generated-src";
import { generateMcmUrl } from "../../../helpers/logUtils";

interface Props {
  items: PsnsRedriveSummary[];
  loading: boolean;
  stage: Stage;
  onOpen: (executionArn: string) => void;
  onRefresh: () => void;
}

const STATUS: Record<
  string,
  { type: StatusIndicatorProps.Type; label: string }
> = {
  RUNNING: { type: "in-progress", label: "Running" },
  SUCCEEDED: { type: "success", label: "Succeeded" },
  FAILED: { type: "error", label: "Failed" },
  TIMED_OUT: { type: "error", label: "Timed out" },
  ABORTED: { type: "stopped", label: "Aborted" },
};

const fileName = (s3Uri: string) => s3Uri.split("/").pop() || s3Uri;

const columns = (
  stage: Stage,
  onOpen: (executionArn: string) => void
): TableProps.ColumnDefinition<PsnsRedriveSummary>[] => [
  {
    id: "startDate",
    header: "Started",
    cell: (item) => (
      <Link
        href={`?stage=${encodeURIComponent(
          stage
        )}&executionArn=${encodeURIComponent(item.executionArn)}`}
        onFollow={(event) => {
          event.preventDefault();
          onOpen(item.executionArn);
        }}
      >
        {item.startDate ? new Date(item.startDate).toLocaleString() : "-"}
      </Link>
    ),
  },
  {
    id: "type",
    header: "Type",
    cell: (item) => {
      if (item.dryRun === undefined) {
        return "-";
      }
      return item.dryRun ? (
        <Badge>Dry run</Badge>
      ) : (
        <Badge color="blue">Redrive</Badge>
      );
    },
  },
  {
    id: "status",
    header: "Status",
    cell: (item) => {
      const status = STATUS[item.status];
      return status ? (
        <StatusIndicator type={status.type}>{status.label}</StatusIndicator>
      ) : (
        item.status || "-"
      );
    },
  },
  {
    id: "executor",
    header: "Started by",
    cell: (item) => item.executor || "-",
  },
  {
    id: "file",
    header: "File",
    cell: (item) =>
      item.eventsS3Uri ? (
        <span title={item.eventsS3Uri}>{fileName(item.eventsS3Uri)}</span>
      ) : (
        "-"
      ),
  },
  {
    id: "eventCount",
    header: "Events",
    cell: (item) => item.eventCount ?? "-",
  },
  {
    id: "mcm",
    header: "MCM",
    cell: (item) =>
      item.mcmId ? (
        <Link external href={generateMcmUrl(stage, item.mcmId)}>
          {item.mcmId}
        </Link>
      ) : (
        "-"
      ),
  },
];

export function PsnsRedrivesTable({
  items,
  loading,
  stage,
  onOpen,
  onRefresh,
}: Props) {
  return (
    <Table
      resizableColumns
      columnDefinitions={columns(stage, onOpen)}
      items={items}
      loading={loading}
      loadingText="Loading recent PSNS redrives"
      header={
        <Header
          counter={items.length > 0 ? `(${items.length})` : undefined}
          description={`The ${stage} stage's most recent dry runs and redrives, newest first.`}
          actions={
            <Button
              iconName="refresh"
              ariaLabel="Refresh recent PSNS redrives"
              onClick={onRefresh}
              disabled={loading}
              data-testid="refresh-psns-redrives"
            />
          }
        >
          Recent PSNS redrives
        </Header>
      }
      empty={<Box textAlign="center">No PSNS redrives in {stage} yet</Box>}
    />
  );
}
